import apiClient from "@shared/services/apiClient";
import {
  LOGIN_CONSENT_STORAGE_KEYS,
  createLoginConsentRecordId,
  getLoginConsentPolicyVersion,
} from "@shared/config/loginConsentPolicy";
import type { LoginComplianceProfile } from "@shared/config/loginComplianceProfile";
import { getOrganizationSlugFromHostname } from "@shared/utils/orgLoginRouting";
import { getBrowserTelemetry } from "@shared/services/telemetry";

export type LoginConsentTelemetryEvent = "accepted" | "declined";

interface RecordLoginConsentEventInput {
  event: LoginConsentTelemetryEvent;
  consentRecordId?: string;
  complianceProfile: LoginComplianceProfile;
  locale?: string;
  termsAccepted?: boolean;
  privacyAccepted?: boolean;
  dataProcessingAccepted?: boolean;
}

export async function recordLoginConsentEvent(
  input: RecordLoginConsentEventInput,
): Promise<string | null> {
  const consentRecordId =
    input.consentRecordId?.trim() || createLoginConsentRecordId();
  const policyVersion = getLoginConsentPolicyVersion(input.complianceProfile);

  try {
    const browserTelemetry = getBrowserTelemetry();
    await apiClient.post("/auth/login-consent-event", {
      ...browserTelemetry,
      event: input.event,
      consent_record_id: consentRecordId,
      policy_version: policyVersion,
      compliance_profile: input.complianceProfile,
      locale: input.locale,
      terms_accepted: input.termsAccepted === true,
      privacy_accepted: input.privacyAccepted === true,
      data_processing_accepted: input.dataProcessingAccepted === true,
      org_slug: getOrganizationSlugFromHostname(window.location.hostname),
      event_ts: new Date().toISOString(),
    });
  } catch {
    // Best-effort only. Must not block login UX.
  }

  if (input.event === "accepted") {
    try {
      localStorage.setItem(LOGIN_CONSENT_STORAGE_KEYS.recordId, consentRecordId);
      localStorage.setItem(
        LOGIN_CONSENT_STORAGE_KEYS.policyVersion,
        policyVersion,
      );
      localStorage.setItem(
        LOGIN_CONSENT_STORAGE_KEYS.profile,
        input.complianceProfile,
      );
    } catch {
      // Ignore storage failures.
    }
  }

  return consentRecordId;
}
